import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { getForms, saveResponse } from 'services/forms';
import { getGroups } from 'services/groups.service';
import { Helmet } from 'react-helmet-async';
import PageWrapper from 'components/PageWrapper';
import Typography from '@mui/material/Typography';
import Form from 'components/form';
import Button from 'components/Button';
import Spin from 'components/Spin';
import FormField from 'modules/forms/components/FormField';
import { getMediaUrl, uploadMedia } from 'services/media';
import useRequest from 'hooks/useRequest';
import toast from 'utils/toast';

const { FormItem } = Form;

const ViewForm = ({
  match: {
    params: { id },
  },
  form,
}) => {
  const account = useSelector((s) => s.settings.account);
  const user = useSelector((s) => s.user.user);
  const [submitted, setSubmitted] = useState(false);
  const [data, { loading, error }] = useRequest(getForms, { hash: id });
  const { getFieldDecorator, validateFields } = form;
  const formData = !loading ? data[0] : null;

  const onSubmit = () => {
    return validateFields().then(async (values) => {
      try {
        const result = {};
        const keys = Object.keys(values);
        for (const key of keys) {
          const field = formData.form_fields_json.fields.find(
            (f) => f.fieldName === key,
          );
          if (!field && key === 'group_ids') {
            result[key] = values[key];
            continue;
          }
          switch (field.fieldType) {
            case 'date': {
              result[key] = values[key]?.format('YYYY-MM-DD');
              break;
            }
            case 'file': {
              const file = values[key]?.[0];
              if (file) {
                const { location } = await uploadMedia(file);
                result[key] = await getMediaUrl(location);
              }
              break;
            }
            default: {
              result[key] = values[key];
            }
          }
        }
        await saveResponse({
          form_id: formData.form_id,
          form_name: formData.form_name,
          account_id: account.account_id,
          contact_id: user.contact_id,
          form_response: JSON.stringify(result),
        });
        setSubmitted(true);
      } catch (e) {
        toast.error(e.message);
      }
    });
  };

  const groupOptions = formData?.form_groups
    ? formData.form_groups.map((g) => ({
        label: g.group_name,
        value: g.group_id,
      }))
    : [];

  return (
    <PageWrapper
      error={error || (!loading && !formData && 'Form not found')}
      loading={loading}
    >
      <Helmet>
        <title>Form</title>
      </Helmet>
      {formData && (
        <Box>
          <Box mb={4}>
            <Typography variant="h1" gutterBottom>
              {formData.form_name}
            </Typography>
            <Typography variant="h4">
              {formData.form_fields_json.form_description}
            </Typography>
          </Box>
          {submitted ? (
            <Box textAlign="center">
              <Typography variant="h1">Thank You</Typography>
              <Typography variant="h3">
                A member of our team will contact you soon.
              </Typography>
            </Box>
          ) : (
            <Box
              bgcolor="background.paper"
              maxWidth={700}
              borderRadius={1}
              p={2}
            >
              <Form>
                {formData.form_fields_json.fields.map((field) => (
                  <FormItem key={field.id}>
                    {getFieldDecorator(field.fieldName, {
                      rules: [
                        { required: field.isRequired, message: 'Required' },
                      ],
                    })(<FormField field={field} />)}
                  </FormItem>
                ))}
                {groupOptions && (
                  <FormItem>
                    {getFieldDecorator('group_ids', {
                      rules: [
                        { required: true, message: 'Please select groups' },
                      ],
                    })(
                      <FormField
                        field={{
                          options: groupOptions,
                          fieldType: 'checkbox',
                          fieldLabel: 'Groups',
                          fieldHint:
                            'Please select the groups you would like us to create for you.',
                        }}
                      />,
                    )}
                  </FormItem>
                )}
              </Form>
              <Box mt={2} textAlign="right">
                <Button onClick={onSubmit}>Submit</Button>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </PageWrapper>
  );
};

export default Form.create()(ViewForm);
